import { supabase, isConfigured } from './supabase';
import type { ValuationResult } from '../types';

/**
 * Get a valuation for a single property.
 *
 * v1 calls the `valmate_naive_valuation` security-definer RPC,
 * which projects the property's most recent Land Registry sale
 * forward at a flat regional growth rate (currently 4.5% YoY) and
 * always reports `confidence: 'low'`. Honest about being a starting
 * point for a conversation rather than a real RICS valuation.
 *
 * The RPC signature is shape-compatible with the comparables-weighted
 * engine that will replace it (SPEC.md §6 / GAPS.md §1.1) — same
 * fields, same nullability — so the front-end won't change when we
 * upgrade the underlying maths.
 */
export async function getValuation(uprn: string): Promise<ValuationResult | null> {
  if (!isConfigured) return null;
  if (!uprn) return null;

  try {
    const { data, error } = await supabase.rpc('valmate_naive_valuation', {
      p_uprn: uprn,
    });
    if (error) {
      console.warn('valmate_naive_valuation error', error);
      return null;
    }
    return (data ?? null) as ValuationResult | null;
  } catch (err) {
    console.warn('valmate_naive_valuation threw', err);
    return null;
  }
}

/**
 * Format a numeric £ value for display ("£385,000" / "£1.2m").
 * Uses millions for anything ≥ £1m to keep the headline tight.
 */
export function formatMoney(n: number): string {
  if (!isFinite(n)) return '—';
  if (n >= 1_000_000) {
    return `£${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 2)}m`;
  }
  if (n >= 1_000) {
    return `£${Math.round(n / 1_000)}k`;
  }
  return `£${Math.round(n).toLocaleString('en-GB')}`;
}

/**
 * Format an ISO date as "July 2015". Used for "last sold" copy.
 */
export function formatMonthYear(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}
